/*
 * Dionis cart subscription conversion (CDIONIS-94), inline selector build 2026-09-10.
 * One native <select> per eligible cart line; a change is the shopper's choice
 * and runs ONE verified cart operation, exactly like a quantity change.
 * The cart-update engine below (config, sections, validation, native apply,
 * recovery, verification) is unchanged from the reviewed dialog build; only
 * the interface layer around it changed.
 */
(function () {
  'use strict';
  if (window.DionisSubConvert) return; // one controller instance per document; a second load must not double-POST
  const CONFIG_ID = 'dionis-sub-convert-config';
  const SELECT = '[data-sub-select]';
  const DEFAULT_TIMEOUT = 15000;

  /* Locale strings arrive HTML-escaped from the t filter; every consumer here
     writes them with textContent, which does not decode entities. Decode once
     at parse. A detached textarea never executes markup. */
  const DECODE_BOX = document.createElement('textarea');
  function decodeEntities(value) {
    if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
    DECODE_BOX.innerHTML = value;
    return DECODE_BOX.value;
  }
  let cfg = null;
  let live = null;
  let inFlight = false;
  let epoch = 0;
  let reloadRequired = false;
  let bound = false;
  let pendingState = null;   // { data, root, view } while an operation is in flight
  let reloadState = null;    // { key, message } once recovery needs a reload
  function loadConfig() {
    const node = document.getElementById(CONFIG_ID);
    if (!node) return null;
    try {
      const data = JSON.parse(node.textContent);
      if (!(data && data.enabled === true && data.strings)) return null;
      Object.keys(data.strings).forEach(function (key) {
        data.strings[key] = decodeEntities(data.strings[key]);
      });
      return data;
    } catch (error) { console.warn('[SubConvert] Invalid configuration; controls remain hidden.'); return null; }
  }
  function nativeClass() {
    const C = customElements.get('cart-element');
    if (!C || !['fillBasket', 'updateTotals', 'updateAllHtml'].every(name => typeof C[name] === 'function')) return null;
    if (!Array.isArray(C.sections) || !C.sections.length || C.sections.length > 5) return null;
    return C;
  }
  function route(name) {
    const routes = (window.Shopify && window.Shopify.routes) || {};
    const path = routes[name];
    if (typeof path === 'string' && path.startsWith('/')) return path.replace(/\.js$/, '') + '.js';
    const root = (routes.root || '/').replace(/\/?$/, '/');
    return root + (name === 'cart_change' ? 'cart/change.js' : 'cart.js');
  }
  function properties(value) {
    if (!value || Array.isArray(value)) return {};
    return Object.fromEntries(Object.keys(value).sort().map(key => [key, String(value[key])]));
  }
  function propertyKey(value) { return JSON.stringify(properties(value)); }
  function selectedPlan(item) {
    return Number(item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.id) || 0;
  }
  function variant(item) { return Number(item.variant_id || item.id); }
  function validCart(cart) {
    return !!cart && Array.isArray(cart.items) && Number.isInteger(cart.item_count) && Number.isFinite(cart.total_price);
  }
  function signature(cart) { return cart.items.map(item => `${item.key}:${item.quantity}:${selectedPlan(item)};`).join(''); }
  function sectionContext() {
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete('sections');
    url.searchParams.delete('section_id');
    return url.pathname + url.search;
  }
  function expectedSections(C) {
    const names = new Set(C.sections);
    // Include hidden cart-page views even if a trailing slash/locale caused the
    // vendor's pathname equality test to initialize its list to drawer-only.
    document.querySelectorAll('cart-element[data-view]').forEach(root => {
      if (['drawer', 'desktop', 'mobile'].includes(root.dataset.view)) names.add(`cart-${root.dataset.view}`);
    });
    const result = Array.from(names);
    if (!result.length || result.length > 5 || result.some(name => !/^cart-(drawer|desktop|mobile)$/.test(name))) {
      throw new Error('Unsupported native section list.');
    }
    return result;
  }
  async function requestJSON(url, options = {}) {
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), DEFAULT_TIMEOUT);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options,
        headers: { Accept: 'application/json', ...(options.headers || {}) }, signal: abort.signal });
      let data;
      try { data = await response.json(); } catch (error) { throw new Error('Invalid JSON response.'); }
      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}`);
        error.status = response.status;
        throw error;
      }
      return data;
    } finally { clearTimeout(timeout); }
  }
  async function readCart() {
    const cart = await requestJSON(route('cart'));
    if (!validCart(cart)) throw new Error('Invalid cart response.');
    return cart;
  }
  function validSections(map, expected, cart) {
    if (!map || typeof map !== 'object') return false;
    return expected.every(name => {
      if (typeof map[name] !== 'string' || !map[name].trim()) return false;
      const doc = new DOMParser().parseFromString(map[name], 'text/html');
      const root = doc.querySelector(`cart-element[data-view="${name.slice(5)}"]`);
      if (!root || !root.querySelector('.cart--form') || !root.querySelector('.cart--body') || !root.querySelector('.cart--totals')) return false;
      const state = root.querySelector('[data-cart-intelligence-state]');
      return !!state && state.dataset.itemSignature === signature(cart) &&
        Number(state.dataset.cartTotal) === cart.total_price && Number(state.dataset.cartItemCount) === cart.item_count;
    });
  }
  function visibleStateMatches(cart, expected) {
    return expected.every(name => {
      const root = document.querySelector(`cart-element[data-view="${name.slice(5)}"]`);
      const state = root && root.querySelector('[data-cart-intelligence-state]');
      return !!state && state.dataset.itemSignature === signature(cart) &&
        Number(state.dataset.cartTotal) === cart.total_price && Number(state.dataset.cartItemCount) === cart.item_count;
    });
  }
  function unchangedSnapshot(before, after) {
    return validCart(before) && validCart(after) && signature(before) === signature(after) &&
      before.total_price === after.total_price && before.item_count === after.item_count &&
      Number(before.total_discount || 0) === Number(after.total_discount || 0);
  }
  async function applyNative(cart, sections, expected) {
    const C = nativeClass();
    if (!C || !validCart(cart) || !validSections(sections, expected, cart)) throw new Error('Native refresh validation failed.');
    const relevant = Object.fromEntries(expected.map(name => [name, sections[name]]));
    C.fillBasket(cart.items);
    C.updateTotals(cart.item_count);
    C.updateAllHtml(relevant); // NO transitions flag. Native emits theme:cart:updated.
    await new Promise(resolve => requestAnimationFrame(resolve));
    // Event listeners can fail without propagating an exception to dispatchEvent.
    // Verify the rendered carriers before treating all surfaces as refreshed.
    if (!visibleStateMatches(cart, expected)) throw new Error('The native cart views did not refresh.');
    document.dispatchEvent(new CustomEvent('cart:updated')); // Existing Bold hook, once.
    reveal();
  }
  async function recover(expected) {
    // Read-only recovery. A failed response (even HTTP 400) does not prove that
    // Shopify rejected the mutation. Never send that POST a second time.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const cart = await readCart();
      const url = new URL(sectionContext(), window.location.origin);
      url.searchParams.set('sections', expected.join(','));
      const sections = await requestJSON(url.pathname + url.search);
      if (validSections(sections, expected, cart)) {
        await applyNative(cart, sections, expected);
        return cart;
      }
    }
    throw new Error('Read-only recovery could not obtain consistent sections.');
  }
  function sameIdentity(item, target) {
    return variant(item) === target.variantId && propertyKey(item.properties) === target.propertiesKey;
  }
  function groupQuantity(cart, target, plan) {
    return cart.items.filter(item => sameIdentity(item, target) && selectedPlan(item) === plan)
      .reduce((sum, item) => sum + Number(item.quantity), 0);
  }
  function mutationVerified(before, after, target, desired) {
    if (!validCart(after)) return false;
    const oldGroupBefore = groupQuantity(before, target, target.oldPlan);
    const newGroupBefore = groupQuantity(before, target, desired);
    return groupQuantity(after, target, target.oldPlan) === oldGroupBefore - target.quantity &&
      groupQuantity(after, target, desired) === newGroupBefore + target.quantity;
  }
  function element(tag, className, text) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text !== undefined) el.textContent = text;
    return el;
  }
  function announce(message) {
    if (!live) {
      live = element('div', 'dsc-visually-hidden');
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      document.body.appendChild(live);
    }
    live.textContent = '';
    requestAnimationFrame(() => { live.textContent = message; });
  }
  /* ------------------------------ interface ------------------------------ */
  function parseSelect(select) {
    const holder = select.closest('[data-sub-line]');
    if (!holder) return null;
    const key = select.dataset.lineKey;
    const variantId = Number(select.dataset.variantId);
    if (!key || !Number.isSafeInteger(variantId) || variantId <= 0) return null;
    const plans = [];
    const ids = new Set();
    let hasOneTime = false;
    for (const option of Array.from(select.options)) {
      if (option.value === '') { hasOneTime = true; continue; }
      const id = Number(option.value);
      if (!Number.isSafeInteger(id) || id <= 0 || ids.has(id)) return null;
      ids.add(id);
      plans.push({ id, name: option.textContent.trim() });
    }
    if (!plans.length) return null;
    const currentPlan = Number(select.dataset.currentPlan || 0);
    const required = select.dataset.required === 'true';
    // Do not silently preselect another plan when the active plan disappeared.
    if (currentPlan && !ids.has(currentPlan)) return null;
    if (required && hasOneTime) return null;
    return { key, variantId, currentPlan, required, plans, holder };
  }
  function reveal() { arm(); } // name kept for the unchanged engine's post-refresh call
  function ready() { cfg = loadConfig(); return !!cfg && !!nativeClass(); }
  function arm() {
    // Refresh hooks (native updates, section loads, manual refresh) call this
    // freely. It must never undo a lock: while an operation is in flight or a
    // reload is required, every selector stays disabled and the visible
    // pending/reload state is re-rendered into whatever markup now exists.
    if (inFlight || reloadRequired) {
      document.querySelectorAll(SELECT).forEach(select => { select.disabled = true; });
      if (inFlight && pendingState) renderPending(pendingState);
      if (reloadRequired && reloadState) renderReload();
      return;
    }
    const ok = ready();
    document.querySelectorAll(SELECT).forEach(select => {
      const valid = ok && !!parseSelect(select);
      select.disabled = !valid;
      if (valid) select.value = String(Number(select.dataset.currentPlan || 0) || '');
    });
  }
  function rootOf(select) { return select.closest('cart-element'); }
  function findSelect(root, key) {
    const scope = root && root.isConnected ? root : document;
    return Array.from(scope.querySelectorAll(SELECT)).find(el => el.dataset.lineKey === key) || null;
  }
  function renderPending(state) {
    const root = state.root.isConnected ? state.root : document.querySelector(`cart-element[data-view="${state.view}"]`);
    const select = findSelect(root, state.data.key);
    if (!select) return;
    select.disabled = true;
    select.setAttribute('aria-busy', 'true');
    const status = select.closest('[data-sub-line]').querySelector('[data-sub-line-status]');
    if (status) { status.textContent = cfg.strings.pending; status.hidden = false; }
  }
  function clearPending(state) {
    const root = state.root.isConnected ? state.root : document.querySelector(`cart-element[data-view="${state.view}"]`);
    const select = findSelect(root, state.data.key);
    if (!select) return;
    select.removeAttribute('aria-busy');
    const status = select.closest('[data-sub-line]').querySelector('[data-sub-line-status]');
    if (status) { status.textContent = ''; status.hidden = true; }
  }
  /* The temporary lock. While one operation is in flight, no other cart
     action may start: not a click, not a keyboard activation, not a form
     submit, not a quantity change, and not a control inside an app's shadow
     root (capture-phase listeners on the document run before shadow-internal
     handlers and stopPropagation keeps them from running). The gift-message
     textarea is exempt so typing is never interrupted. Restored the moment
     the operation settles; nothing outside this controller is changed. */
  function gate(event) {
    if (!inFlight) return;
    const target = event.target;
    if (!target || !target.closest || !target.closest('cart-element')) return;
    if (target.matches && target.matches('textarea')) return;
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }
  function setPending(state, pending) {
    document.querySelectorAll('cart-element').forEach(el => {
      if (pending) { el.setAttribute('data-dsc-busy', 'true'); el.setAttribute('aria-busy', 'true'); }
      else { el.removeAttribute('data-dsc-busy'); el.removeAttribute('aria-busy'); }
    });
    if (pending) {
      pendingState = state;
      document.querySelectorAll(SELECT).forEach(select => { select.disabled = true; });
      renderPending(state);
      announce(cfg.strings.pending);
    } else {
      clearPending(state);
      pendingState = null;
    }
  }
  function revertSelect(state) {
    const select = findSelect(state.root, state.data.key);
    if (select) select.value = String(state.data.currentPlan || '');
  }
  function lineError(state, message) {
    revertSelect(state);
    const select = findSelect(state.root, state.data.key);
    const error = select && select.closest('[data-sub-line]').querySelector('[data-sub-line-error]');
    if (error) { error.textContent = message; error.hidden = false; }
    announce(message);
  }
  function clearError(select) {
    const error = select.closest('[data-sub-line]').querySelector('[data-sub-line-error]');
    if (error) { error.textContent = ''; error.hidden = true; }
  }
  function reloadHolder(root) {
    // Per view: the matching line's error slot, else the first eligible line,
    // else a notice at the top of that view's summary (survives native swaps,
    // which replace .cart--totals but never the summary shell).
    const select = findSelect(root, reloadState.key) || root.querySelector(SELECT);
    const inLine = select && select.closest('[data-sub-line]').querySelector('[data-sub-line-error]');
    if (inLine) return inLine;
    const summary = root.querySelector('.cart--summary');
    if (!summary) return null;
    const existing = summary.querySelector('.dsc-cart-notice');
    if (existing) return existing;
    const notice = element('p', 'dsc-line__error dsc-cart-notice');
    notice.setAttribute('role', 'alert');
    return summary.insertBefore(notice, summary.firstChild);
  }
  function renderReloadInto(root) {
    // Idempotent per view. A button in another view (hidden or not) never
    // satisfies this view's check, so every cart view carries its own action.
    if (root.querySelector('.dsc-line__reload')) return;
    const select = findSelect(root, reloadState.key) || root.querySelector(SELECT);
    let holder = select && select.closest('[data-sub-line]').querySelector('[data-sub-line-error]');
    if (!holder) {
      const summary = root.querySelector('.cart--summary');
      if (!summary) return;
      holder = summary.querySelector('.dsc-cart-notice') || summary.insertBefore(element('p', 'dsc-line__error dsc-cart-notice'), summary.firstChild);
      holder.setAttribute('role', 'alert');
    }
    holder.textContent = reloadState.message;
    holder.hidden = false;
    const reload = element('button', 'dsc-line__reload', cfg.strings.reload);
    reload.type = 'button';
    reload.addEventListener('click', () => window.location.reload());
    holder.appendChild(document.createElement('br'));
    holder.appendChild(reload);
  }
  function renderReload() {
    // Called on lock and again after every cart rebuild. On /cart the drawer,
    // desktop, and mobile views coexist and only one is shown (and which one
    // can change with the viewport), so each view renders its own action.
    if (!reloadState) return;
    document.querySelectorAll('cart-element').forEach(renderReloadInto);
  }
  function reloadButtonToFocus() {
    const initiating = document.querySelector(`cart-element[data-view="${reloadState.view}"] .dsc-line__reload`);
    if (initiating && initiating.getClientRects().length) return initiating;
    const buttons = Array.from(document.querySelectorAll('.dsc-line__reload'));
    return buttons.find(button => button.getClientRects().length) || initiating || buttons[0] || null;
  }
  function lockForReload(state, message) {
    // The cart's true state is unknown: freeze every purchase-option control
    // until the shopper reloads. Never re-POST, never guess.
    reloadRequired = true;
    reloadState = { key: state.data.key, message, view: state.view };
    document.querySelectorAll(SELECT).forEach(select => { select.disabled = true; });
    renderReload();
    const button = reloadButtonToFocus();
    if (button) button.focus({ preventScroll: true }); else window.location.reload();
    announce(message);
  }
  function focusAfter(state, after, target, desired) {
    const root = state.root.isConnected ? state.root : document.querySelector(`cart-element[data-view="${state.view}"]`);
    const items = after.items.filter(item => sameIdentity(item, target) && selectedPlan(item) === desired);
    if (items.length === 1 && root) {
      const select = Array.from(root.querySelectorAll(SELECT)).find(el => el.dataset.lineKey === items[0].key && !el.disabled);
      if (select) { select.focus({ preventScroll: true }); return; }
    }
    if (root) {
      const heading = root.querySelector('.cart--title');
      if (heading) { if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1'); heading.focus({ preventScroll: true }); }
    }
  }
  async function applyChoice(select) {
    if (inFlight || reloadRequired || !cfg) { select.value = String(Number(select.dataset.currentPlan || 0) || ''); return; }
    const data = parseSelect(select);
    if (!data) { select.disabled = true; return; }
    const desired = Number(select.value) || 0;
    if ((data.required && desired === 0) || (desired !== 0 && !data.plans.some(plan => plan.id === desired))) { select.value = String(data.currentPlan || ''); return; }
    if (desired === data.currentPlan) return; // opening the menu or reselecting sends nothing
    const root = rootOf(select);
    const state = { data, root, view: (root && root.dataset.view) || 'drawer' };
    clearError(select);
    const C = nativeClass();
    if (!C) { lineError(state, cfg.strings.unavailable); return; }
    let expected;
    try { expected = expectedSections(C); }
    catch (error) { lineError(state, cfg.strings.unavailable); return; }
    const startEpoch = epoch;
    inFlight = true;
    setPending(state, true);
    let posted = false;
    let before;
    let target;
    let after;
    let success = false;
    let uncertain = false;
    let message = cfg.strings.error;
    try {
      before = await readCart();
      const index = before.items.findIndex(item => item.key === data.key);
      if (index < 0 || epoch !== startEpoch) { message = cfg.strings.changed; return; }
      const item = before.items[index];
      if (properties(item.properties)._Gifted || variant(item) !== data.variantId || selectedPlan(item) !== data.currentPlan) {
        message = cfg.strings.changed;
        return;
      }
      if (!Number.isInteger(item.quantity) || item.quantity < 1) { message = cfg.strings.changed; return; }
      target = { variantId: variant(item), propertiesKey: propertyKey(item.properties), oldPlan: selectedPlan(item), quantity: item.quantity };
      // This is the ONLY mutation path. Shopify requires a fresh line index and
      // explicit quantity for selling_plan changes. Properties are not sent.
      posted = true;
      const result = await requestJSON(route('cart_change'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: index + 1, quantity: item.quantity, selling_plan: desired || null,
          sections: expected, sections_url: sectionContext() })
      });
      if (!validCart(result)) throw new Error('Mutation response is not a cart.');
      if (validSections(result.sections, expected, result)) {
        await applyNative(result, result.sections, expected);
        after = result;
      } else after = await recover(expected);
      success = mutationVerified(before, after, target, desired);
      if (!success) { message = cfg.strings.changed; uncertain = true; }
    } catch (error) {
      console.warn('[SubConvert] The cart operation needs recovery:', error.message);
      if (posted) {
        try {
          after = await recover(expected);
          success = !!target && mutationVerified(before, after, target, desired);
          if (!success) {
            // A known HTTP rejection with a verified unchanged cart may be tried
            // again by the shopper. Network/parse errors remain ambiguous.
            uncertain = !(error.status >= 400 && error.status < 500 && unchangedSnapshot(before, after));
            message = uncertain ? cfg.strings.unknown : cfg.strings.error;
          }
        } catch (recoveryError) {
          uncertain = true;
          message = cfg.strings.unknown;
          console.warn('[SubConvert] Native recovery unavailable; reload required.');
        }
      } else message = cfg.strings.error;
    } finally {
      inFlight = false;
      setPending(state, false);
      if (success) {
        announce(cfg.strings.updated);
        arm();
        focusAfter(state, after, target, desired);
      } else if (uncertain) {
        lockForReload(state, message);
      } else {
        arm(); // release the lock on every retryable outcome
        lineError(state, message);
      }
    }
  }
  function onNativeUpdate() {
    epoch += 1;
    queueMicrotask(arm); // arm() re-renders pending or reload state when locked
  }
  function init() {
    cfg = loadConfig();
    if (!bound) {
      bound = true;
      ['click', 'submit', 'keydown', 'change', 'input'].forEach(type => document.addEventListener(type, gate, true));
      document.addEventListener('change', event => {
        const select = event.target && event.target.matches && event.target.matches(SELECT) ? event.target : null;
        if (select) applyChoice(select);
      });
      window.addEventListener('theme:cart:updated', onNativeUpdate);
      document.addEventListener('shopify:section:load', arm);
      window.addEventListener('pageshow', event => {
        if (event.persisted) { inFlight = false; pendingState = null; reloadRequired = false; reloadState = null; document.querySelectorAll('cart-element').forEach(el => { el.removeAttribute('data-dsc-busy'); el.removeAttribute('aria-busy'); }); arm(); }
      });
    }
    arm();
    customElements.whenDefined('cart-element').then(arm);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  window.DionisSubConvert = Object.freeze({
    refresh: arm,
    diagnostics: () => ({ enabled: !!cfg, nativeReady: !!nativeClass(), inFlight, reloadRequired })
  });
})();